// The arithmetic behind the Attendance summary. Pure functions over the
// per-service attendance records, so the screen only lays the answers out.

import { amountOf, dateOf, isoOf, totalBy, lastMonths, recent, inYear, sumOf } from '../../utils/summaryStats';
import type { NamedCount } from '../../utils/summaryStats';

export interface AttendanceLike {
  recordId?: string;
  date?: string;
  serviceTime?: string;
  eventName?: string;
  venue?: string;
  /** Total headcount. */
  attendees?: unknown;
  menCount?: unknown;
  womenCount?: unknown;
  youthCount?: unknown;
  childrenCount?: unknown;
  newVisitors?: unknown;
  status?: string;
}

/** A total with its average per service. */
export interface AverageRow { name: string; services: number; total: number; average: number; }
export interface AverageMonth { month: string; label: string; services: number; total: number; average: number; }

/** A service whose count needs correcting, and why. */
export interface AttendanceIssue { record: AttendanceLike; reason: string; }

export interface AttendanceSummary {
  services: number;
  totalAttendance: number;
  /** Average headcount per service, all time; null with no records. */
  average: number | null;
  highest: AttendanceLike | null;
  /** Average of each Sunday's combined headcount over the last 12 weeks. */
  sundayAverage: number | null;
  sundaysCounted: number;
  services30: number;
  average30: number | null;
  visitorsYear: number;
  visitors30: number;
  /** Men / Women / Youth / Children (and what was not broken down), all time. */
  mix: NamedCount[];
  months: AverageMonth[];
  byEvent: AverageRow[];
  /** Highest and lowest counted services of the last 12 months (never overlapping). */
  best: AttendanceLike[];
  worst: AttendanceLike[];
  issues: AttendanceIssue[];
  /** Sundays of the last 8 weeks with no service recorded at all. */
  missedSundays: string[];
}

const headcount = (r: AttendanceLike) => amountOf(r.attendees);
const breakdown = (r: AttendanceLike) =>
  amountOf(r.menCount) + amountOf(r.womenCount) + amountOf(r.youthCount) + amountOf(r.childrenCount);
const avg = (total: number, n: number): number => (n > 0 ? Math.round(total / n) : 0);

/** True when a yyyy-MM-dd date falls on a Sunday. */
export const isSunday = (v: unknown): boolean => {
  const d = dateOf(v);
  if (!d) return false;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).getDay() === 0;
};

/** The `count` Sundays before today (today itself left out), newest first. */
export const sundaysBefore = (count: number, now: Date = new Date()): string[] => {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - (d.getDay() === 0 ? 7 : d.getDay()));
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(isoOf(d));
    d.setDate(d.getDate() - 7);
  }
  return out;
};

export const attendanceSummary = (rows: AttendanceLike[], now: Date = new Date()): AttendanceSummary => {
  const records = rows.filter((r) => String(r.status || '').toUpperCase() !== 'VOID');
  const counted = records.filter((r) => headcount(r) > 0);
  const total = sumOf(records, (r) => r.attendees);

  // Sunday attendance: every service on the same Sunday added together.
  const perSunday = new Map<string, number>();
  recent(counted, (r) => r.date, 84, now).filter((r) => isSunday(r.date)).forEach((r) => {
    const d = dateOf(r.date) as string;
    perSunday.set(d, (perSunday.get(d) || 0) + headcount(r));
  });
  const sundayTotals = Array.from(perSunday.values());

  const last30 = recent(counted, (r) => r.date, 30, now);
  const lastYear = recent(counted, (r) => r.date, 365, now);

  const unsplit = records.reduce((n, r) => n + Math.max(0, headcount(r) - breakdown(r)), 0);
  const mix: NamedCount[] = [
    { name: 'Men', count: sumOf(records, (r) => r.menCount) },
    { name: 'Women', count: sumOf(records, (r) => r.womenCount) },
    { name: 'Youth', count: sumOf(records, (r) => r.youthCount) },
    { name: 'Children', count: sumOf(records, (r) => r.childrenCount) },
    ...(unsplit > 0 ? [{ name: 'Not broken down', count: unsplit }] : [])
  ];

  const issues: AttendanceIssue[] = [];
  records.forEach((r) => {
    const split = breakdown(r);
    if (headcount(r) <= 0) issues.push({ record: r, reason: 'No headcount recorded' });
    else if (split > 0 && split !== headcount(r)) {
      issues.push({ record: r, reason: `Breakdown adds up to ${split}, total says ${headcount(r)}` });
    }
  });
  issues.sort((a, b) => String(dateOf(b.record.date)).localeCompare(String(dateOf(a.record.date))));

  const recorded = new Set(records.map((r) => dateOf(r.date)));
  const byHeadcount = [...lastYear].sort((a, b) => headcount(b) - headcount(a));

  return {
    services: records.length,
    totalAttendance: total,
    average: counted.length ? avg(total, counted.length) : null,
    highest: counted.reduce<AttendanceLike | null>((top, r) => (!top || headcount(r) > headcount(top) ? r : top), null),
    sundayAverage: sundayTotals.length ? avg(sundayTotals.reduce((n, v) => n + v, 0), sundayTotals.length) : null,
    sundaysCounted: sundayTotals.length,
    services30: last30.length,
    average30: last30.length ? avg(sumOf(last30, (r) => r.attendees), last30.length) : null,
    visitorsYear: sumOf(inYear(records, (r) => r.date, now.getFullYear()), (r) => r.newVisitors),
    visitors30: sumOf(recent(records, (r) => r.date, 30, now), (r) => r.newVisitors),
    mix,
    months: lastMonths(counted, (r) => r.date, 12, now, (r) => r.attendees)
      .map((m) => ({ month: m.month, label: m.label, services: m.count, total: m.total, average: avg(m.total, m.count) })),
    byEvent: totalBy(counted, (r) => r.eventName, (r) => r.attendees)
      .map((e) => ({ name: e.name, services: e.count, total: e.total, average: avg(e.total, e.count) })),
    // The lowest five come from what is left after the top five, so a short
    // history never lists one service as both.
    best: byHeadcount.slice(0, 5),
    worst: byHeadcount.slice(5).reverse().slice(0, 5),
    issues,
    missedSundays: sundaysBefore(8, now).filter((d) => !recorded.has(d))
  };
};
